/**
 * 환생 시스템 정의 (PLAN.md 기반)
 */

/**
 * 1차 환생: 블록체인
 * 조건: 총 생산량 1M 이상
 * 보상: blockchain = √(totalEarned / 1M)
 */
export function calculateBlockchainReward(totalEarned: number): number {
  if (totalEarned < 1e6) return 0;
  return Math.floor(Math.sqrt(totalEarned / 1e6));
}

/**
 * 블록체인 효과: 탭 파워 & CPS 배율
 * 배율 = 1 + (blockchain × 0.01)
 */
export function getBlockchainMultiplier(blockchain: number): number {
  return 1 + blockchain * 0.01;
}

/**
 * 2차 환생: 메타버스 코인
 * 해금 조건: 1차 환생 5회 이상 + 블록체인 50개 이상
 * 환생 조건: 블록체인 100개 이상
 * 보상: metaCoin = √(blockchain / 100)
 */
export function canUnlockPrestige2(prestigeCount: number, blockchain: number): boolean {
  return prestigeCount >= 5 && blockchain >= 50;
}

export function canPrestige2(blockchain: number): boolean {
  return blockchain >= 100;
}

export function calculateMetaCoinReward(blockchain: number): number {
  if (blockchain < 100) return 0;
  return Math.floor(Math.sqrt(blockchain / 100));
}

/**
 * 메타버스 코인 업그레이드 (5종)
 */
export interface MetaUpgradeConfig {
  id: string;
  name: string;
  description: string;
  cost: number;
  effect: string;
}

export const META_UPGRADES: MetaUpgradeConfig[] = [
  {
    id: 'meta_auto_tap',
    name: '자동 탭봇',
    description: '1초마다 자동 탭 (탭 파워 100%)',
    cost: 1,
    effect: 'autoTap'
  },
  {
    id: 'meta_golden_boost',
    name: '골든 확률 증가',
    description: '골든 이벤트 출현 확률 2배',
    cost: 2,
    effect: 'goldenChance'
  },
  {
    id: 'meta_offline_time',
    name: '오프라인 시간 연장',
    description: '오프라인 보너스 8시간 → 16시간',
    cost: 3,
    effect: 'offlineTime'
  },
  {
    id: 'meta_milestone_boost',
    name: '마일스톤 부스트',
    description: '마일스톤 ×2 → ×3',
    cost: 5,
    effect: 'milestoneBoost'
  },
  {
    id: 'meta_prestige_amplify',
    name: '환생 보너스 증폭',
    description: '블록체인 효과 1% → 2%',
    cost: 10,
    effect: 'prestigeAmplify'
  }
];

/**
 * 블록체인 버프 구매 옵션 (환생 재화로 구매)
 */
export interface PrestigeBuffConfig {
  id: string;
  name: string;
  description: string;
  baseCost: number;
  costMultiplier: number;
  maxLevel: number;
  effect: {
    type: 'clickPower' | 'cps' | 'goldenChance' | 'offlineRate';
    value: number; // % per level
  };
}

export const PRESTIGE_BUFFS: PrestigeBuffConfig[] = [
  {
    id: 'buff_click',
    name: '클릭 파워 증폭',
    description: '레벨당 탭 파워 +5%',
    baseCost: 1,
    costMultiplier: 2,
    maxLevel: 50,
    effect: { type: 'clickPower', value: 5 }
  },
  {
    id: 'buff_cps',
    name: 'CPS 증폭',
    description: '레벨당 CPS +5%',
    baseCost: 1,
    costMultiplier: 2,
    maxLevel: 50,
    effect: { type: 'cps', value: 5 }
  },
  {
    id: 'buff_golden',
    name: '골든 확률',
    description: '레벨당 골든 확률 +2%',
    baseCost: 3,
    costMultiplier: 2.5,
    maxLevel: 20,
    effect: { type: 'goldenChance', value: 2 }
  },
  {
    id: 'buff_offline',
    name: '오프라인 효율',
    description: '레벨당 오프라인 수익률 +5%',
    baseCost: 5,
    costMultiplier: 3,
    maxLevel: 10,
    effect: { type: 'offlineRate', value: 5 }
  }
];
